import { useRef, useState } from 'react';
import { productCategories } from '@/mocks/products';

export default function ProductShowcase() {
  const [visibleCount, setVisibleCount] = useState<Record<string, number>>({});

  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Featured Products
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            Browse curated finds from the Kakobuy Spreadsheet. Click any item to view details and purchase.
          </p>
        </div>

        {productCategories.map((cat) => (
          <CategoryRow
            key={cat.name}
            category={cat}
            visibleCount={visibleCount[cat.name] || 4}
            onShowMore={() =>
              setVisibleCount((prev) => ({
                ...(prev || {}),
                [cat.name]: ((prev || {})[cat.name] || 4) + 4,
              }))
            }
          />
        ))}
      </div>
    </section>
  );
}

function CategoryRow({
  category,
  visibleCount,
  onShowMore,
}: {
  category: (typeof productCategories)[number];
  visibleCount: number;
  onShowMore: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const showAll = visibleCount >= category.products.length;

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = direction === 'left' ? -320 : 320;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="mb-10 md:mb-12">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
          <span className="w-1.5 h-5 rounded-full bg-primary-500" />
          {category.name}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-base md:text-lg" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-base md:text-lg" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 md:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {category.products.slice(0, visibleCount).map((product) => (
          <a
            key={product.id}
            href={product.link}
            target="_blank"
            rel="noopener nofollow"
            className="snap-start flex-shrink-0 w-44 sm:w-48 md:w-56 group block"
          >
            <div className="bg-white rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:shadow-sm hover:border-primary-200">
              <div className="w-full h-44 sm:h-48 md:h-56 overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-3 md:p-4">
                <h4 className="text-foreground-950 text-xs md:text-sm font-medium leading-tight line-clamp-2 mb-2 min-h-[2.5em]">
                  {product.name}
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-primary-500 font-bold text-sm md:text-base">
                    {product.priceUSD}
                  </span>
                  <span className="text-foreground-400 text-xs md:text-sm">
                    {product.priceCNY}
                  </span>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {!showAll && category.products.length > 4 && (
        <div className="text-center mt-3">
          <button
            onClick={onShowMore}
            className="inline-flex items-center gap-1.5 text-primary-500 font-medium text-sm hover:text-primary-600 transition-colors cursor-pointer"
          >
            Show more
            <i className="ri-arrow-down-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}